interface Props {
  scores: { experimentName: string; percentage: number }[];
}

function getBarColor(pct: number): string {
  if (pct >= 80) return 'var(--secondary, #00A86B)';
  if (pct >= 60) return '#3B82F6';
  if (pct >= 40) return '#F59E0B';
  return '#EF4444';
}

export default function QuizScoreChart({ scores }: Props) {
  if (scores.length === 0) {
    return (
      <div className="quiz-chart-empty">
        <p>No quiz scores yet. Complete experiment quizzes to see your results here.</p>
        <style>{chartStyles}</style>
      </div>
    );
  }

  return (
    <div className="quiz-score-chart">
      {scores.map((item, idx) => {
        const barColor = getBarColor(item.percentage);
        return (
          <div key={idx} className="chart-row">
            <div className="chart-label" title={item.experimentName}>
              {item.experimentName}
            </div>
            <div className="chart-bar-wrapper">
              <div
                className="chart-bar-fill"
                style={{
                  width: `${Math.min(Math.max(item.percentage, 0), 100)}%`,
                  background: barColor,
                }}
              />
            </div>
            <span className="chart-value" style={{ color: barColor }}>
              {Math.round(item.percentage)}%
            </span>
          </div>
        );
      })}
      {/* Legend */}
      <div className="chart-legend">
        <span className="legend-item">
          <span className="legend-dot" style={{ background: 'var(--secondary, #00A86B)' }} />
          80%+
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: '#3B82F6' }} />
          60-79%
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: '#F59E0B' }} />
          40-59%
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: '#EF4444' }} />
          &lt;40%
        </span>
      </div>
      <style>{chartStyles}</style>
    </div>
  );
}

const chartStyles = `
  .quiz-chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    color: var(--text-muted, #999999);
    text-align: center;
  }
  .quiz-chart-empty p {
    font-size: 13px;
    max-width: 300px;
    line-height: 1.5;
    margin: 0;
  }
  .quiz-score-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .chart-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .chart-label {
    width: 140px;
    flex-shrink: 0;
    font-size: 12px;
    color: var(--text-secondary, #CCCCCC);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .chart-bar-wrapper {
    flex: 1;
    height: 20px;
    background: var(--bg-dark, #1A1A1A);
    border-radius: 4px;
    overflow: hidden;
    min-width: 80px;
  }
  .chart-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.5s ease;
    min-width: 2px;
  }
  .chart-value {
    width: 40px;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
  }
  .chart-legend {
    display: flex;
    gap: 16px;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid var(--border, #4A4A4A);
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-muted, #999999);
  }
  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  @media (max-width: 640px) {
    .chart-label {
      width: 100px;
      font-size: 11px;
    }
    .chart-legend {
      flex-wrap: wrap;
      gap: 10px;
    }
  }
`;
